import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { normalizeSiteHref } from '../../lib/siteRoutes';
import { CtaFormModal } from './CtaFormModal';
import { SearchPanel } from './SearchPanel';
import { OptimizedImage } from './OptimizedImage';

const HEADER_QUICK_LINKS = [
  { label: 'Products', href: '/products' },
  { label: 'Technology', href: '/technologies' },
  { label: 'Case Studies', href: '/case-studies' },
  { label: 'Leadership', href: '/leadership' },
  { label: 'Our Story', href: '/our-story' },
  { label: 'Careers', href: '/careers' },
  { label: 'FAQ', href: '/faq' },
];

function Brand({ variant = 'wordmark', className = '' }) {
  const isBadge = variant === 'badge';
  const logoSrc = isBadge ? 'https://images.ctfassets.net/bjia12x471fb/5I3OIMUqV1G3lXISdaq33c/33ec06355d216ac1307f97c30e904332/rivelin_logos_rivelin_logo-idevice_gr-drk_4x.png' : 'https://images.ctfassets.net/bjia12x471fb/5I3OIMUqV1G3lXISdaq33c/33ec06355d216ac1307f97c30e904332/rivelin_logos_rivelin_logo-idevice_gr-drk_4x.png';

  return (
    <Link to='/' className={`brand-lockup ${className}`.trim()} aria-label='Rivelin Robotics home'>
      <OptimizedImage
        src={logoSrc}
        alt='Rivelin Robotics'
        className={`brand-logo ${isBadge ? 'brand-logo-badge' : ''}`.trim()}
        fit='contain'
        sizes={isBadge ? '(max-width: 640px) 44px, 56px' : '(max-width: 640px) 184px, 244px'}
        width={isBadge ? 512 : 1536}
        height={isBadge ? 512 : 1024}
        loading='eager'
        decoding='sync'
      />
    </Link>
  );
}

function SearchToggle({ onClick }) {
  return (
    <button className='search-toggle' type='button' onClick={onClick} aria-label='Open site search' aria-expanded='false'>
      <svg viewBox='0 0 24 24' focusable='false'>
        <circle
          cx='11'
          cy='11'
          r='5.5'
          fill='none'
          stroke='currentColor'
          strokeWidth='1.8'
        />
        <path d='M15.2 15.2 19.5 19.5' fill='none' stroke='currentColor' strokeLinecap='round' strokeWidth='1.8' />
      </svg>
    </button>
  );
}

function HamburgerMenu({ items, onNavigate }) {
  const [open, setOpen] = React.useState(false);
  const timerRef = React.useRef(null);
  const close = () => setOpen(false);

  const handleMouseLeave = () => {
    timerRef.current = setTimeout(close, 1500);
  };
  const handleMouseEnter = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  return (
    <div className='hamburger-menu' onMouseLeave={handleMouseLeave} onMouseEnter={handleMouseEnter}>
      <button
        className='hamburger-toggle'
        type='button'
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={`hamburger-icon ${open ? 'open' : ''}`} aria-hidden='true'>
          <span /><span /><span />
        </span>
      </button>
      {open && (
        <nav className='hamburger-dropdown' aria-label='Site navigation'>
          {items.map((item) => (
            <Link
              key={item.href}
              className='hamburger-link'
              to={normalizeSiteHref(item.href)}
              onClick={() => { close(); onNavigate?.(); }}
            >
              {item.label}
            </Link>
          ))}
        </nav>
      )}
    </div>
  );
}

function Header({ isSearchOpen, onSearchOpen, onSearchClose, onCloseMenus, shellRef }) {
  return (
    <header className='site-header' ref={shellRef}>
      <div className='site-header-shell'>
        <HamburgerMenu items={HEADER_QUICK_LINKS} onNavigate={onCloseMenus} />
        <Brand variant='badge' className='site-header-badge' />
        <div className='header-actions'>
          <div className={`header-search ${isSearchOpen ? 'open' : ''}`.trim()}>
            {isSearchOpen ? <SearchPanel isOpen={isSearchOpen} onClose={onSearchClose} /> : <SearchToggle onClick={onSearchOpen} />}
          </div>
        </div>
      </div>
    </header>
  );
}

function getExplicitModalType(value) {
  if (value === 'contact' || value === 'demoRequest' || value === 'jobApplication') {
    return value;
  }

  return '';
}

function getCtaModalFromAnchor(anchor) {
  const explicitType = getExplicitModalType(anchor.dataset.ctaModal);
  const explicitRole = anchor.dataset.ctaRole || '';

  if (explicitType) {
    return { type: explicitType, role: explicitRole };
  }

  let url;

  try {
    url = new URL(anchor.href, window.location.origin);
  } catch (error) {
    return null;
  }

  if (url.origin !== window.location.origin) {
    return null;
  }

  const pathname = url.pathname.replace(/\/$/, '') || '/';

  if (pathname === '/request-demo' || pathname === '/request-a-demo' || pathname === '/datasheet') {
    return { type: 'demoRequest' };
  }

  if (pathname === '/contact' || pathname === '/contact-us') {
    return { type: 'contact' };
  }

  if (url.hash === '#job-application' && (pathname === '/careers' || pathname === '/')) {
    return { type: 'jobApplication', role: url.searchParams.get('role') || explicitRole };
  }

  return null;
}

function FooterIcon({ type }) {
  if (type === 'email') {
    return (
      <svg className='footer-icon' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
        <rect x='2' y='4' width='16' height='12' rx='2' stroke='currentColor' strokeWidth='1.5' />
        <path d='M2 7l8 5 8-5' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
      </svg>
    );
  }
  if (type === 'phone') {
    return (
      <svg className='footer-icon' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
        <path d='M4 3h3.5l1.5 4-2 1.5a11 11 0 004.5 4.5L13 11l4 1.5V16a1 1 0 01-1 1C6.5 17 3 10.5 3 4a1 1 0 011-1z' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
      </svg>
    );
  }
  if (type === 'linkedin') {
    return (
      <svg className='footer-icon' viewBox='0 0 20 20' fill='currentColor' aria-hidden='true'>
        <path d='M5.5 7h-3v9h3V7zm-1.5-.9a1.6 1.6 0 110-3.2 1.6 1.6 0 010 3.2zM17 16h-3v-4.5c0-1.1-.4-1.8-1.4-1.8-.8 0-1.2.5-1.4 1-.1.2-.1.5-.1.8V16H8s.04-8 0-9h3v1.3c.4-.6 1.1-1.5 2.7-1.5 2 0 3.3 1.3 3.3 4V16z' />
      </svg>
    );
  }
  if (type === 'location') {
    return (
      <svg className='footer-icon' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
        <path d='M10 2C6.7 2 4 4.7 4 8c0 4.5 6 10 6 10s6-5.5 6-10c0-3.3-2.7-6-6-6z' stroke='currentColor' strokeWidth='1.5' />
        <circle cx='10' cy='8' r='2' stroke='currentColor' strokeWidth='1.5' />
      </svg>
    );
  }
  return null;
}

function Footer({ footer, contact }) {
  return (
    <footer className='site-footer'>
      <div className='site-footer-shell'>
        <div className='footer-brand-col'>
          <Brand />
          {footer.summary ? <p className='footer-summary'>{footer.summary}</p> : null}
        </div>
        <div className='footer-contact-col'>
          <p className='footer-col-heading'>Contact</p>
          <div className='footer-contact'>
            <div className='footer-contact-stack footer-contact-stack-primary'>
              <a className='footer-contact-item' href={`mailto:${contact.email}`}>
                <FooterIcon type='email' />
                {contact.email}
              </a>
              {contact.phone ? (
                <a className='footer-contact-item' href={`tel:${contact.phone.replace(/\s/g, '')}`}>
                  <FooterIcon type='phone' />
                  {contact.phone}
                </a>
              ) : null}
            </div>
            <div className='footer-contact-stack footer-contact-stack-secondary'>
              <a className='footer-contact-item' href={contact.socials?.[0]?.href || 'https://www.linkedin.com/company/rivelin-robotics'} target='_blank' rel='noreferrer'>
                <FooterIcon type='linkedin' />
                LinkedIn
              </a>
              <a className='footer-contact-item' href='https://maps.google.com/?q=Unit+3+Neepsend+Triangle+1+Burton+Rd+Sheffield+S3+8BW' target='_blank' rel='noreferrer'>
                <FooterIcon type='location' />
                Rivelin HQ
              </a>
              <a className='footer-contact-item' href='https://maps.google.com/?q=Unit+5+Rivelin+Factory+Rutland+Business+Park+Neepsend+Sheffield+S3+8BD' target='_blank' rel='noreferrer'>
                <FooterIcon type='location' />
                Rivelin Factory
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className='footer-bottom'>
        <span>{footer.legalNote}</span>
        <span>©{new Date().getFullYear()} Rivelin<span className='footer-registered-mark' aria-hidden='true'>®</span> Robotics Limited</span>
        {footer.legalLinks?.length ? (
          <span className='footer-legal-links'>
            {footer.legalLinks.map((link) => (
              <Link key={link.href} to={link.href}>{link.label}</Link>
            ))}
          </span>
        ) : null}
      </div>
    </footer>
  );
}

export function SiteLayout({ siteSettings, jobs = [], children }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [ctaModal, setCtaModal] = useState(null);
  const shellRef = useRef(null);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (shellRef.current && !shellRef.current.contains(event.target)) {
        setSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  const closeMenus = () => {
    setSearchOpen(false);
  };

  const openSearch = () => {
    setSearchOpen(true);
  };

  const closeCtaModal = useCallback(() => {
    setCtaModal(null);
  }, []);

  const handleCtaClickCapture = (event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const anchor = event.target.closest?.('a');

    if (!anchor || (anchor.target && anchor.target !== '_self')) {
      return;
    }

    const modal = getCtaModalFromAnchor(anchor);

    if (!modal) {
      return;
    }

    event.preventDefault();
    setSearchOpen(false);
    setCtaModal(modal);
  };

  return (
    <div className='site-shell' onClickCapture={handleCtaClickCapture}>
      <Header
        isSearchOpen={searchOpen}
        onSearchOpen={openSearch}
        onSearchClose={() => setSearchOpen(false)}
        onCloseMenus={closeMenus}
        shellRef={shellRef}
      />
      <main className='site-main'>{children}</main>
      <Footer footer={siteSettings.footer} contact={siteSettings.contact} />
      <Link className='floating-cta primary-cta' to='/request-demo'>Book Demo</Link>
      <CtaFormModal modal={ctaModal} onClose={closeCtaModal} jobs={jobs} contact={siteSettings.contact} />
    </div>
  );
}
